import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChartRenderer from '../components/ChartRenderer';
import ReportSidebar from '../components/ReportSidebar';
import MethodologyAppendix from '../components/MethodologyAppendix';
import ChatPanel from '../components/ChatPanel';
import LintWarning from '../components/LintWarning';
import {
    Typography,
    Button,
    Box,
    Paper,
    FormControl,
    FormControlLabel,
    Switch,
    TextField,
    Autocomplete,
    CircularProgress,
    Alert,
    Divider,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import apiService from '../services/api';

const ReportGenerator = ({
    brands,
    categories,
    brand,
    category,
    year,
    country,
    onBrandChange,
    onCategoryChange,
    onCountryChange,
    countries,
    loadingData,
    error,
    setError,
    report,
    setReport
}) => {
    const navigate = useNavigate();
    const { reportId } = useParams();
    const [loading, setLoading] = useState(false);
    const [copySnackbar, setCopySnackbar] = useState(false);
    const [progress, setProgress] = useState(null);
    const [requestId, setRequestId] = useState(null);
    const [selectedReportId, setSelectedReportId] = useState(null);
    // The report is lifted into <App/> and persists across navigation, but
    // `selectedReportId` is local and resets whenever this component remounts
    // (e.g. right after navigating to /report-generator/:id, or returning via
    // the menu). Fall back to the id in the URL so the fix controls / auto-fix
    // slider stay available whenever a saved report is on screen.
    const effectiveReportId = selectedReportId || reportId;
    const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
    const progressIntervalRef = useRef(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [appendixExpanded, setAppendixExpanded] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [reportUpdatedSnackbar, setReportUpdatedSnackbar] = useState(false);
    const [fixingLint, setFixingLint] = useState(false);
    const [lintFixSnackbar, setLintFixSnackbar] = useState('');
    // Auto-fix: the user opts in from the Generate New Report form BEFORE
    // generating. When on, we send one fix request over the linter findings as
    // soon as the freshly-generated report is available — before the findings
    // are ever surfaced. (The backend runs up to 5 fix/re-check rounds within
    // that request.) `autoFixDoneRef` ensures we send it only once.
    const [autoFixEnabled, setAutoFixEnabled] = useState(false);
    const autoFixDoneRef = useRef(false);
    // Mirror of `autoFixEnabled` so the progress-polling closure (which is set up
    // once per generation) can read the current value without being re-created.
    const autoFixEnabledRef = useRef(autoFixEnabled);
    // True while the pre-surface auto-fix round is running: we keep a progress
    // state on screen and hold the report (and its warning box) back until the
    // round finishes, so the user never sees the un-fixed findings flash.
    const [autoFixPending, setAutoFixPending] = useState(false);
    const appendixRef = useRef(null);

    useEffect(() => {
        autoFixEnabledRef.current = autoFixEnabled;
    }, [autoFixEnabled]);

    // Load specific report from URL parameter
    useEffect(() => {
        console.log('useEffect triggered - reportId:', reportId, 'selectedReportId:', selectedReportId);
        if (reportId && reportId !== selectedReportId) {
            console.log('Conditions met, calling loadReportSession');
            loadReportSession(reportId);
        } else {
            console.log('Conditions not met for loading report session');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId, selectedReportId]);

    // Function to load a specific report session
    const loadReportSession = async (reportSessionId) => {
        try {
            console.log('Loading report session:', reportSessionId);
            setError('');
            
            // Clear any ongoing generation state to prevent conflicts
            setRequestId(null);
            setProgress(null);
            
            // Clear any existing polling interval
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
            }
            
            const response = await apiService.getReportSession(reportSessionId);
            console.log('Report session response:', response.data);
            
            if (response.data.report_data) {
                console.log('Setting report data for session:', reportSessionId);
                
                // Update form values based on loaded report only if they differ
                if (brand !== response.data.brand) {
                    onBrandChange(response.data.brand);
                }
                if (response.data.category && category !== response.data.category) {
                    onCategoryChange(response.data.category);
                }
                if (country !== response.data.country) {
                    onCountryChange(response.data.country);
                }
                
                // Initialize first section as expanded
                const initialExpanded = {};
                if (response.data.report_data.sections) {
                    initialExpanded[0] = true;
                }
                
                // Batch all state updates together to prevent multiple re-renders
                setReport(response.data.report_data);
                setSelectedReportId(reportSessionId);
                setExpandedSections(initialExpanded);
            } else {
                setError('Report data not available for this session');
            }
        } catch (err) {
            console.error('Error loading report session:', err);
            setError('Failed to load report session');
        }
    };

    // Handle report selection from sidebar
    const handleReportSelect = (reportSessionId) => {
        console.log('handleReportSelect called with:', reportSessionId);
        // Switching reports closes the (report-specific) chat panel
        setChatOpen(false);
        // Don't set selectedReportId here - let loadReportSession handle it
        navigate(`/report-generator/${reportSessionId}`);
    };

    // Handle new report button
    const handleNewReport = () => {
        setSelectedReportId(null);
        setReport(null);
        setError('');
        setProgress(null);
        setRequestId(null);
        setExpandedSections({});
        setChatOpen(false);
        navigate('/report-generator');
    };

    // Function to poll for progress updates and get the report when complete
    useEffect(() => {
        if (requestId) {
            // Clear any existing interval
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }

            // Set up polling for progress updates
            progressIntervalRef.current = setInterval(async () => {
                try {
                    const progressResponse = await apiService.getReportProgress(requestId);
                    console.log("Progress update:", progressResponse.data);
                    setProgress(progressResponse.data);

                    // If the report is complete, get the report data from the progress updates
                    if (progressResponse.data.status === 'complete' && progressResponse.data.report_data) {
                        console.log("Report complete, setting report data");
                        const completedReport = progressResponse.data.report_data;
                        // If the user opted into auto-fix, keep a progress state on
                        // screen (rather than surfacing the un-fixed report) whenever
                        // the fresh report has findings. The auto-fix effect below
                        // runs the single fix round and clears this flag.
                        const completedFindings =
                            completedReport.lint && Array.isArray(completedReport.lint.findings)
                                ? completedReport.lint.findings
                                : [];
                        if (autoFixEnabledRef.current && completedFindings.length > 0) {
                            setAutoFixPending(true);
                        }
                        setReport(completedReport);
                        setSelectedReportId(requestId); // Use request ID as report session ID
                        setLoading(false);
                        clearInterval(progressIntervalRef.current);
                        progressIntervalRef.current = null;
                        
                        // Update URL to include the report ID
                        navigate(`/report-generator/${requestId}`);
                        
                        // Refresh sidebar to show new report
                        setSidebarRefreshTrigger(prev => prev + 1);
                        
                        // Initialize first section as expanded
                        if (progressResponse.data.report_data.sections) {
                            const initialExpanded = {};
                            initialExpanded[0] = true;
                            setExpandedSections(initialExpanded);
                        }
                    }
                    // If there's an error, stop polling
                    else if (progressResponse.data.status === 'error') {
                        setError(`Error generating report: ${progressResponse.data.message}`);
                        setLoading(false);
                        clearInterval(progressIntervalRef.current);
                        progressIntervalRef.current = null;
                    }
                } catch (err) {
                    console.error('Error fetching progress:', err);
                    // If there's an error fetching progress, stop polling
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                }
            }, 1000); // Poll every second

            // Clean up interval on unmount
            return () => {
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                }
            };
        }
    }, [requestId, setReport, navigate, setError]);

    // Toggle the in-window chat panel for the current report.
    // The report stays on screen as a canvas; the assistant can edit it live.
    const handleChatWithReport = () => {
        if (report) {
            setChatOpen((open) => !open);
        }
    };

    // Apply a live edit the assistant made to the report
    const handleReportUpdated = (updatedReport) => {
        setReport(updatedReport);
        setReportUpdatedSnackbar(true);
    };

    // Send the report + the linter findings the user KEPT (they can ignore
    // individual warnings) to the LLM and ask it to rewrite the statements that
    // contradict the data, then update the view in place.
    const handleFixLint = async (keptFindings) => {
        if (!effectiveReportId) {
            setError('Save or open the report before fixing its warnings.');
            return;
        }
        try {
            setFixingLint(true);
            setError('');
            const response = await apiService.fixReportLint(effectiveReportId, keptFindings);
            if (response.data && response.data.report_data) {
                setReport(response.data.report_data);
                const remaining = response.data.report_data.lint;
                const remainingFail = remaining ? (remaining.fail || 0) : 0;
                const remainingWarn = remaining ? (remaining.warn || 0) : 0;
                if (remainingFail === 0 && remainingWarn === 0) {
                    setLintFixSnackbar('✨ All warnings fixed');
                } else {
                    setLintFixSnackbar(
                        `Updated the report — ${remainingFail + remainingWarn} warning${remainingFail + remainingWarn === 1 ? '' : 's'} remain`
                    );
                }
            }
        } catch (err) {
            console.error('Error fixing report warnings:', err);
            setError('Failed to fix the report warnings. Please try again.');
        } finally {
            setFixingLint(false);
        }
    };

    // Toggle the auto-fix slider. Turning it off re-arms the one-round guard so
    // the user can run another single round later if they re-enable it.
    const handleAutoFixToggle = (enabled) => {
        setAutoFixEnabled(enabled);
        if (!enabled) {
            autoFixDoneRef.current = false;
        }
    };

    // A newly-loaded/regenerated report re-arms auto-fix so it can run once for
    // the new report.
    useEffect(() => {
        autoFixDoneRef.current = false;
    }, [effectiveReportId]);

    // When auto-fix is on, send one fix request over the freshly-generated
    // report's findings as soon as they are available — before they are surfaced.
    // The ref caps this at one request so we never loop even if warnings remain
    // afterward (the backend already retries, up to 5 rounds). `autoFixPending` keeps the report hidden behind a progress state
    // until the round finishes.
    useEffect(() => {
        if (!autoFixEnabled || autoFixDoneRef.current) return;
        if (!effectiveReportId || fixingLint) return;
        const findings = report && report.lint ? report.lint.findings : null;
        if (!Array.isArray(findings) || findings.length === 0) {
            // Clean report (or findings cleared) — nothing to fix, reveal it.
            setAutoFixPending(false);
            return;
        }
        autoFixDoneRef.current = true;
        setAutoFixPending(true);
        Promise.resolve(handleFixLint(findings)).finally(() => setAutoFixPending(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFixEnabled, report, effectiveReportId, fixingLint]);

    // Function to handle sharing the report
    const handleShareReport = () => {
        if (selectedReportId) {
            const currentUrl = window.location.origin;
            const shareLink = `${currentUrl}/share/report/${selectedReportId}`;
            setShareUrl(shareLink);
            setShareDialogOpen(true);
        }
    };

    // Function to copy share URL to clipboard
    const handleCopyShareUrl = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopySnackbar(true);
            setShareDialogOpen(false);
        } catch (err) {
            console.error('Failed to copy URL:', err);
        }
    };

    const handleSubmit = async (e) => {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }

        if (!brand) {
            setError('Please select a brand');
            return;
        }

        try {
            // Clear everything immediately when user clicks generate
            setLoading(true);
            setError('');
            setReport(null);
            setRequestId(null);
            setSelectedReportId(null);
            setExpandedSections({});
            setChatOpen(false);

            // Navigate to base report generator URL to clear any existing reportId
            navigate('/report-generator');
            
            // Set initial progress state to show loading immediately
            setProgress({
                status: 'starting',
                message: 'Initializing report generation...',
                steps_completed: 0,
                total_steps: 1,
                current_step: 'Starting'
            });

            // Create form data
            const formData = new FormData();
            formData.append('brand', brand);
            if (category) {
                formData.append('category', category);
            }
            if (country) {
                formData.append('country', country);
            }
            if (year) {
                formData.append('year', year);
            }

            // Make API request - this now returns just the request ID
            const response = await apiService.generateReport(formData);

            if (response.data.request_id) {
                console.log("Got request ID:", response.data.request_id);
                setRequestId(response.data.request_id);

                // Try to get initial progress
                try {
                    const progressResponse = await apiService.getReportProgress(response.data.request_id);
                    console.log("Initial progress:", progressResponse.data);
                    setProgress(progressResponse.data);
                } catch (err) {
                    console.error("Error getting initial progress:", err);
                    // Keep the initial progress state if we can't get the real one yet
                }

                // Keep loading state true while we poll for progress
                // The loading state will be set to false when the report is complete
            } else {
                setError('Unexpected response format. No request ID returned.');
                setLoading(false);
                setProgress(null);
            }
        } catch (err) {
            console.error('Error generating report:', err);
            // Handle the improved error response from the backend
            if (err.response?.data?.detail && typeof err.response.data.detail === 'object' && err.response.data.detail.message) {
                // This is the case where the brand exists but not in the specified category
                const errorDetail = err.response.data.detail;
                const availableCategories = errorDetail.available_categories || [];

                let errorMessage = errorDetail.message;
                if (availableCategories.length > 0) {
                    errorMessage += `. This brand is available in the following categories: ${availableCategories.join(', ')}`;
                }

                setError(errorMessage);

                // Auto-select the first available category if there are any
                if (availableCategories.length > 0) {
                    onCategoryChange(availableCategories[0]);
                }
            } else {
                // Handle other error types
                const errorMessage = err.response?.data?.detail
                    ? (typeof err.response.data.detail === 'object'
                        ? JSON.stringify(err.response.data.detail)
                        : err.response.data.detail)
                    : 'Failed to generate report. Please try again.';
                setError(errorMessage);
            }
            setLoading(false);
        }
    };

    console.log(report)

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            // Don't clip/over-constrain height here: the chat panel relies on
            // `position: sticky` (broken by an `overflow: hidden` ancestor), and
            // the page itself scrolls with the document.
            px: 1, // Reduced horizontal padding
            py: 2,
            pt: 1
        }}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        TrustSpark.ai Trust Report
                    </Typography>
                    <Typography variant="body1">
                        Comprehensive trust analysis report for brands and categories using AI-powered insights together with BrandSpark proprietary Trust Survey data.
                    </Typography>
                </Box>
            </Box>

            {/* AI Disclaimer */}
            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Disclaimer:</strong> TrustSpark.ai uses an AI model and can hallucinate or make mistakes. Please double-check all outputs. By using TrustSpark.ai, you acknowledge the possibility of error and agree to assume all liability for decisions made on the basis of AI outputs.
            </Alert>

            {/* Main Content Area with Sidebar */}
            <Box sx={{ 
                display: 'flex', 
                flexGrow: 1,
                gap: 2,
                minHeight: 0
            }}>
                {/* Report Sidebar */}
                <ReportSidebar
                    selectedReportId={selectedReportId}
                    onReportSelect={handleReportSelect}
                    onNewReport={handleNewReport}
                    selectedCountry={country}
                    refreshTrigger={sidebarRefreshTrigger}
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                />

                {/* Main Report Content */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    minWidth: 0,
                    minHeight: 0
                }}>
                    {/* Report Generation Form */}
                    <Paper elevation={2} sx={{ p: 3, mb: 2, flexShrink: 0 }}>
                        <Typography variant="h6" gutterBottom>
                            Generate New Report
                        </Typography>
                        <form onSubmit={handleSubmit}>
                                                         <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                 <FormControl sx={{ minWidth: 200, flex: 1 }}>
                                     <Autocomplete
                                         value={brand}
                                         onChange={(event, newValue) => onBrandChange(newValue)}
                                         options={brands}
                                         renderInput={(params) => <TextField {...params} label="Brand" />}
                                         disabled={loadingData || loading}
                                     />
                                 </FormControl>
                                 
                                 <FormControl sx={{ minWidth: 200, flex: 1 }}>
                                     <Autocomplete
                                         value={category}
                                         onChange={(event, newValue) => onCategoryChange(newValue)}
                                         options={categories}
                                         renderInput={(params) => <TextField {...params} label="Category (Optional)" />}
                                         disabled={loadingData || loading}
                                     />
                                 </FormControl>
                             </Box>
                            
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading || loadingData || !brand}
                                    sx={{ minWidth: 140 }}
                                >
                                    {loading ? (
                                        <>
                                            <CircularProgress size={20} sx={{ mr: 1 }} />
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate Report'
                                    )}
                                </Button>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={autoFixEnabled}
                                            onChange={(e) => handleAutoFixToggle(e.target.checked)}
                                            disabled={loading || loadingData}
                                        />
                                    }
                                    label="Auto-fix data inconsistencies with AI before showing the report (up to 5 rounds)"
                                />
                            </Box>
                        </form>
                    </Paper>

                    {/* Error and Loading States */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {loadingData && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {/* Canvas (report) + in-window chat panel */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                        flexGrow: 1,
                        minHeight: 0
                    }}>
                        {/* Report canvas (scrolls independently) */}
                        <Box sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            overflow: 'auto'
                        }}>

                    {/* Show progress indicator when we have progress data and report is not yet complete */}
                    {progress && !report && (
                        <Paper elevation={2} sx={{ p: 3, mb: 4, mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CircularProgress size={30} sx={{ mr: 2 }} />
                                <Typography variant="h6">
                                    Generating Report
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                <strong>Current step:</strong> {progress.current_step}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {progress.message}
                            </Typography>
                        </Paper>
                    )}

                    {/* Auto-fix in progress: the report is ready but has findings and
                        the user opted into auto-fix, so we fix them (up to 5 rounds) before
                        surfacing anything. */}
                    {autoFixPending && (
                        <Paper elevation={2} sx={{ p: 3, mb: 4, mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CircularProgress size={30} sx={{ mr: 2 }} />
                                <Typography variant="h6">
                                    Auto-fixing data inconsistencies
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                The report is ready. Fixing statements that contradict the data and
                                re-checking them (up to 5 rounds) before showing it to you. This can
                                take a few minutes…
                            </Typography>
                        </Paper>
                    )}

                    {report && !autoFixPending && (
                        <Box>
                            {/* Data-vs-claims linter results (see backend/report_linter.py).
                                Flags statements that contradict the report's own charts and
                                offers the user a re-generation. */}
                            <LintWarning
                                lint={report.lint}
                                onRegenerate={handleSubmit}
                                regenerating={loading}
                                onFix={effectiveReportId ? handleFixLint : undefined}
                                fixing={fixingLint}
                            />
                            <Card sx={{ mb: 4 }}>
                                <CardContent>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'flex-start',
                                        mb: 2,
                                        gap: 2,
                                        flexWrap: 'wrap'
                                    }}>
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography variant="h5" component="h2" gutterBottom>
                                                {report.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                <a
                                                    href="#methodology-appendix"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setAppendixExpanded(true);
                                                        setTimeout(() => {
                                                            if (appendixRef.current) {
                                                                appendixRef.current.scrollIntoView({ behavior: 'smooth' });
                                                            }
                                                        }, 100);
                                                    }}
                                                    style={{ color: 'inherit' }}
                                                >
                                                    Click here to see the full methodology summary in the appendix.
                                                </a>
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ShareIcon />}
                                                onClick={handleShareReport}
                                                disabled={!selectedReportId}
                                                size="small"
                                            >
                                                Share
                                            </Button>
                                            <Button
                                                variant={chatOpen ? 'outlined' : 'contained'}
                                                color="primary"
                                                onClick={handleChatWithReport}
                                                size="small"
                                            >
                                                {chatOpen ? 'Hide chat' : 'Chat with report'}
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="body1" paragraph>
                                        <strong>Executive Summary:</strong> {report.executive_summary}
                                    </Typography>

                                    {report.data_highlights && report.data_highlights.length > 0 && (
                                        <Box sx={{ mt: 3, mb: 2 }}>
                                            <Typography variant="h6" gutterBottom>
                                                Key Data Highlights
                                            </Typography>
                                            <List>
                                                {report.data_highlights.map((highlight, index) => (
                                                    <ListItem key={index} sx={{ py: 0.5 }}>
                                                        <ListItemText primary={`• ${highlight}`} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                            {report.sections && report.sections.length > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                    <Button
                                        onClick={() => {
                                            const newExpandedSections = {};
                                            report.sections.forEach((_, index) => {
                                                newExpandedSections[index] = true;
                                            });
                                            setExpandedSections(newExpandedSections);
                                        }}
                                        sx={{ mr: 1 }}
                                    >
                                        Expand All
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            const newExpandedSections = {};
                                            report.sections.forEach((_, index) => {
                                                newExpandedSections[index] = false;
                                            });
                                            setExpandedSections(newExpandedSections);
                                        }}
                                    >
                                        Collapse All
                                    </Button>
                                </Box>
                            )}

                            {report.sections && report.sections.map((section, index) => (
                                <Accordion
                                    key={index}
                                    expanded={expandedSections[index] ?? (index === 0)}
                                    onChange={(_, isExpanded) => {
                                        setExpandedSections(prev => ({
                                            ...prev,
                                            [index]: isExpanded
                                        }));
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`panel${index}-content`}
                                        id={`panel${index}-header`}
                                    >
                                        <Typography variant="h6">{section.title}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {section.visualizations && section.visualizations.length > 0 && (
                                            <Box sx={{ mb: 3 }}>
                                                {section.visualizations.map((visualization, vizIndex) => (
                                                    <Box key={vizIndex} sx={{ mb: vizIndex < section.visualizations.length - 1 ? 4 : 0 }}>
                                                        {section.intro && visualization.title === 'Trust Quadrant - All Category Brands' && (
                                                            <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                                {section.intro}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="subtitle1" gutterBottom>
                                                            {visualization.title}
                                                        </Typography>
                                                        <ChartRenderer visualization={visualization} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {section.intro && (!section.visualizations || !section.visualizations.some(v => v.title === 'Trust Quadrant - All Category Brands')) && (
                                            <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                {section.intro}
                                            </Typography>
                                        )}

                                        <Typography variant="body1" paragraph>
                                            {section.content}
                                        </Typography>

                                        {section.subsections && section.subsections.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                {section.subsections.map((subsection, subIndex) => (
                                                    <Box key={subIndex} sx={{ mb: 3, ml: 2 }}>
                                                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                                                            {subsection.title}
                                                        </Typography>

                                                        {subsection.visualizations && subsection.visualizations.length > 0 && (
                                                            <Box sx={{ mb: 2 }}>
                                                                {subsection.visualizations.map((visualization, vizIndex) => (
                                                                    <Box key={vizIndex} sx={{ mb: vizIndex < subsection.visualizations.length - 1 ? 3 : 0 }}>
                                                                        <Typography variant="subtitle2" gutterBottom>
                                                                            {visualization.title}
                                                                        </Typography>
                                                                        <ChartRenderer visualization={visualization} />
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}

                                                        <Typography variant="body2" paragraph>
                                                            {subsection.content}
                                                        </Typography>

                                                        {subsection.key_findings && subsection.key_findings.length > 0 && (
                                                            <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                                                <Typography variant="subtitle2" gutterBottom>
                                                                    Key Findings:
                                                                </Typography>
                                                                <List dense>
                                                                    {subsection.key_findings.map((finding, findingIdx) => (
                                                                        <ListItem key={findingIdx} sx={{ py: 0.5 }}>
                                                                            <ListItemText primary={`• ${finding}`} />
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {section.key_findings && section.key_findings.length > 0 && (
                                            <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    Key Findings:
                                                </Typography>
                                                <List dense>
                                                    {section.key_findings.map((finding, idx) => (
                                                        <ListItem key={idx} sx={{ py: 0.5 }}>
                                                            <ListItemText primary={`• ${finding}`} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )}

                                        {section.plan_sections && section.plan_sections.length > 0 && (
                                            <Box sx={{ mt: 2, bgcolor: '#f0f8ff', p: 2, borderRadius: 1 }}>
                                                <List dense>
                                                    {section.plan_sections.map((planSection, idx) => {
                                                        // Handle both string and object formats
                                                        if (typeof planSection === 'string') {
                                                            return (
                                                                <ListItem key={idx} sx={{ py: 0.5 }}>
                                                                    <ListItemText primary={`• ${planSection}`} />
                                                                </ListItem>
                                                            );
                                                        } else if (typeof planSection === 'object' && planSection !== null) {
                                                            // Handle object format with title and content
                                                            const title = planSection.title || planSection.name || `Section ${idx + 1}`;
                                                            const content = planSection.content || planSection.description || JSON.stringify(planSection);
                                                            return (
                                                                <ListItem key={idx} sx={{ py: 1, alignItems: 'flex-start' }}>
                                                                    <ListItemText 
                                                                        primary={
                                                                            <Box>
                                                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                                                    {title}
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                                    {content}
                                                                                </Typography>
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </List>
                                            </Box>
                                        )}

                                        {section.claims && section.claims.length > 0 && (
                                            <Box sx={{ mt: 2, bgcolor: '#fff8e1', p: 2, borderRadius: 1 }}>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    Marketing Claims:
                                                </Typography>
                                                <List dense>
                                                    {section.claims.map((claim, idx) => (
                                                        <ListItem key={idx} sx={{ py: 0.5 }}>
                                                            <ListItemText primary={`${idx + 1}. ${claim}`} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}

                            {report.recommendations && report.recommendations.length > 0 && (
                                <Card sx={{ mt: 4 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Recommendations
                                        </Typography>
                                        <List>
                                            {report.recommendations.map((recommendation, index) => (
                                                <ListItem
                                                    key={index}
                                                    sx={{ py: 0.5 }}
                                                    secondaryAction={
                                                        <Tooltip title="Copy recommendation">
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="copy"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(recommendation);
                                                                    setCopySnackbar(true);
                                                                }}
                                                                size="small"
                                                            >
                                                                <ContentCopyIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    }
                                                >
                                                    <ListItemText primary={`${index + 1}. ${recommendation}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Methodology Appendix */}
                            <Box ref={appendixRef}>
                                <MethodologyAppendix
                                    country={country}
                                    year={year}
                                    expanded={appendixExpanded}
                                    onChange={(_, isExpanded) => setAppendixExpanded(isExpanded)}
                                />
                            </Box>

                            {/* Contact Info Footer */}
                            <Box sx={{ mt: 4, py: 3, px: 2, borderTop: '1px solid #eee', textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    To learn more about the BrandSpark Most Trusted Awards program, methodology, or for a full list of winning brands, visit{' '}
                                    <a href="https://www.brandsparkmosttrusted.com" target="_blank" rel="noopener noreferrer">
                                        www.brandsparkmosttrusted.com
                                    </a>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Contact: Robert Levy, BrandSpark{' '}
                                    <a href="mailto:RLevy@BrandSpark.com">RLevy@BrandSpark.com</a>
                                </Typography>
                            </Box>

                            <Snackbar
                                open={copySnackbar}
                                autoHideDuration={3000}
                                onClose={() => setCopySnackbar(false)}
                                message="Recommendation copied to clipboard!"
                            />
                        </Box>
                    )}
                        </Box>

                        {/* In-window chat panel — canvas stays on the left.
                            Pinned to the viewport with its own internal scroll so
                            the chat box stays on screen instead of stretching to the
                            full height of the report. */}
                        {chatOpen && report && (
                            <Box sx={{
                                width: { xs: '100%', md: 420 },
                                flexShrink: 0,
                                alignSelf: { md: 'flex-start' },
                                position: { md: 'sticky' },
                                top: { md: 16 },
                                height: { xs: '70vh', md: 'calc(100vh - 32px)' }
                            }}>
                                <ChatPanel
                                    key={selectedReportId || 'current-report'}
                                    report={report}
                                    country={country}
                                    year={year}
                                    reportSessionId={selectedReportId}
                                    onReportUpdated={handleReportUpdated}
                                    onClose={() => setChatOpen(false)}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Share Dialog */}
            <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Share Report
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Share this report with others using the link below. Anyone with this link can view the report without needing to log in.
                    </Typography>
                    <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        border: '1px solid #ddd',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <TextField
                            fullWidth
                            value={shareUrl}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <Tooltip title="Copy link">
                            <IconButton onClick={handleCopyShareUrl} color="primary">
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Note: This link provides read-only access to the report. Viewers cannot generate new reports or access other features.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareDialogOpen(false)}>
                        Close
                    </Button>
                    <Button onClick={handleCopyShareUrl} variant="contained" startIcon={<ContentCopyIcon />}>
                        Copy Link
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Snackbar
                open={copySnackbar}
                autoHideDuration={3000}
                onClose={() => setCopySnackbar(false)}
                message="Link copied to clipboard!"
            />

            <Snackbar
                open={reportUpdatedSnackbar}
                autoHideDuration={3000}
                onClose={() => setReportUpdatedSnackbar(false)}
                message="✨ The assistant updated the report"
            />

            <Snackbar
                open={!!lintFixSnackbar}
                autoHideDuration={4000}
                onClose={() => setLintFixSnackbar('')}
                message={lintFixSnackbar}
            />
        </Box>
    );
};

export default ReportGenerator;
